import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { UseAuth } from '../AuthContext';
import { buildHubConnection } from './buildHubConnection';
import { parseHubConfigs } from './SignalR.config';
import {
  IS_SIGNALR_ENABLED,
  RECONNECT_RETRY_DELAY_MS,
} from './SignalR.constants';
import { inspectConnectionHandlers } from './SignalR.internals';
import { clearManualPing, type ManualPingRefs } from './manualPing';
import { finalizeHubConnectionStart } from './startHubConnection';
import type { HandlerSummary, SignalRContextProps } from './SignalR.types';

const normalizeError = (error: unknown): {
  message: string;
  name: string;
  stack?: string;
} => ({
  message: error instanceof Error ? error.message : String(error),
  name: error instanceof Error ? error.name : 'Unknown',
  stack: error instanceof Error ? error.stack : undefined,
});

export const useSignalRProvider = (): SignalRContextProps => {
  const { isAuthenticated, getAccessToken } = UseAuth();

  const [connections, setConnections] = useState<Map<string, HubConnection>>(
    new Map(),
  );
  const [connectionStates, setConnectionStates] = useState<
    Map<string, HubConnectionState>
  >(new Map());

  const connectionsRef = useRef<Map<string, HubConnection>>(new Map());
  const reconnectTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const isConnectingRef = useRef<Map<string, boolean>>(new Map());
  const pingRefs = useRef<ManualPingRefs>({
    pingIntervals: new Map(),
    sockets: new Map(),
  });

  const updateConnectionState = useCallback(
    (hubName: string, state: HubConnectionState) => {
      setConnectionStates((prev) => {
        const updated = new Map(prev);
        updated.set(hubName, state);
        return updated;
      });
    },
    [],
  );

  const storeSocket = useCallback((hubName: string, socket: WebSocket) => {
    pingRefs.current.sockets.set(hubName, socket);
  }, []);

  const removeSocket = useCallback((hubName: string) => {
    pingRefs.current.sockets.delete(hubName);
  }, []);

  const clearPingInterval = useCallback((hubName: string) => {
    clearManualPing(hubName, pingRefs.current);
  }, []);

  useEffect(() => {
    const initialStates = new Map<string, HubConnectionState>();
    parseHubConfigs().forEach((_, hubName) => {
      initialStates.set(hubName, HubConnectionState.Disconnected);
    });
    setConnectionStates(initialStates);
  }, []);

  const buildConnection = useCallback(
    (hubName: string) =>
      buildHubConnection({
        hubName,
        getAccessToken,
        updateConnectionState,
        storeSocket,
        removeSocket,
        clearPingInterval,
      }),
    [
      getAccessToken,
      updateConnectionState,
      storeSocket,
      removeSocket,
      clearPingInterval,
    ],
  );

  const stopConnection = useCallback(async (hubName: string = 'default') => {
    const timeout = reconnectTimeoutsRef.current.get(hubName);
    if (timeout) {
      clearTimeout(timeout);
      reconnectTimeoutsRef.current.delete(hubName);
    }

    clearManualPing(hubName, pingRefs.current);

    const connection = connectionsRef.current.get(hubName);
    if (!connection) {
      return;
    }

    try {
      console.log(`🛑 Stopping SignalR connection [${hubName}]...`);
      await connection.stop();
      console.log(`✅ SignalR connection [${hubName}] stopped`);
    } catch (error) {
      console.error(`🔴 Error stopping SignalR connection [${hubName}]:`, error);
    } finally {
      connectionsRef.current.delete(hubName);
      setConnections(new Map(connectionsRef.current));
      setConnectionStates((prev) => {
        const updated = new Map(prev);
        updated.set(hubName, HubConnectionState.Disconnected);
        return updated;
      });
    }
  }, []);

  const startConnection = useCallback(
    async (hubName: string = 'default') => {
      if (!IS_SIGNALR_ENABLED) {
        return;
      }

      if (!parseHubConfigs().has(hubName)) {
        console.error(`❌ Hub configuration not found: ${hubName}`);
        return;
      }

      if (isConnectingRef.current.get(hubName)) {
        console.log(`⏳ SignalR connection [${hubName}] already in progress`);
        return;
      }

      if (!isAuthenticated) {
        console.log('⏸️ Skipping SignalR connection - not authenticated');
        return;
      }

      const existingConnection = connectionsRef.current.get(hubName);
      if (existingConnection?.state === HubConnectionState.Connected) {
        console.log(`✅ SignalR [${hubName}] already connected`);
        return;
      }

      try {
        isConnectingRef.current.set(hubName, true);
        console.log(`🚀 Starting SignalR connection [${hubName}]...`);

        if (existingConnection) {
          await existingConnection.stop();
        }

        const newConnection = await buildConnection(hubName);
        connectionsRef.current.set(hubName, newConnection);
        setConnections(new Map(connectionsRef.current));
        updateConnectionState(hubName, newConnection.state);

        await finalizeHubConnectionStart({
          hubName,
          connection: newConnection,
          pingRefs: pingRefs.current,
          storeSocket,
          removeSocket,
        });

        updateConnectionState(hubName, newConnection.state);
      } catch (error) {
        const normalized = normalizeError(error);
        console.error(`🔴 Error starting SignalR connection [${hubName}]:`, {
          ...normalized,
          error,
        });

        setConnectionStates((prev) => {
          const updated = new Map(prev);
          updated.set(
            hubName,
            existingConnection?.state ?? HubConnectionState.Disconnected,
          );
          return updated;
        });

        const timeout = setTimeout(() => {
          if (isAuthenticated && !isConnectingRef.current.get(hubName)) {
            void startConnection(hubName);
          }
        }, RECONNECT_RETRY_DELAY_MS);
        reconnectTimeoutsRef.current.set(hubName, timeout);
      } finally {
        isConnectingRef.current.set(hubName, false);
      }
    },
    [
      isAuthenticated,
      buildConnection,
      updateConnectionState,
      storeSocket,
      removeSocket,
    ],
  );

  const stopAllConnections = useCallback(async () => {
    await Promise.all(
      Array.from(parseHubConfigs().keys()).map((hubName) =>
        stopConnection(hubName),
      ),
    );
  }, [stopConnection]);

  const invokeHub = useCallback(
    async <T = unknown,>(
      hubName: string,
      methodName: string,
      ...args: unknown[]
    ): Promise<T> => {
      const connection = connectionsRef.current.get(hubName);
      if (!connection) {
        throw new Error(`SignalR connection [${hubName}] not established`);
      }

      if (connection.state !== HubConnectionState.Connected) {
        throw new Error(
          `SignalR connection [${hubName}] not connected. Current state: ${connection.state}`,
        );
      }

      try {
        return await connection.invoke<T>(methodName, ...args);
      } catch (error) {
        console.error(
          `🔴 Error invoking SignalR method [${hubName}].${methodName}:`,
          error,
        );
        throw error;
      }
    },
    [],
  );

  const invoke = useCallback(
    async <T = unknown,>(methodName: string, ...args: unknown[]): Promise<T> => {
      if (!parseHubConfigs().has('default')) {
        throw new Error(
          'No default hub configured. Use invokeHub() with a specific hub name, or configure VITE_SIGNALR_HUB_PATH for a default hub.',
        );
      }
      return invokeHub<T>('default', methodName, ...args);
    },
    [invokeHub],
  );

  const getConnection = useCallback((hubName: string = 'default') => {
    return connectionsRef.current.get(hubName) ?? null;
  }, []);

  const getConnectionState = useCallback(
    (hubName: string = 'default') => {
      return connectionStates.get(hubName) ?? HubConnectionState.Disconnected;
    },
    [connectionStates],
  );

  const isConnected = useCallback(
    (hubName: string = 'default') => {
      return getConnectionState(hubName) === HubConnectionState.Connected;
    },
    [getConnectionState],
  );

  const inspectHandlers = useCallback(
    (hubName: string = 'default'): HandlerSummary | undefined => {
      const connection = connectionsRef.current.get(hubName);
      if (!connection) {
        console.warn(`⚠️ No connection found for hub [${hubName}]`);
        return {
          connected: false,
          handlers: {},
          internalKeys: [],
        };
      }

      return inspectConnectionHandlers(hubName, connection);
    },
    [],
  );

  const clearReconnectAndPingResources = useCallback(() => {
    reconnectTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    reconnectTimeoutsRef.current.clear();
    pingRefs.current.pingIntervals.forEach((interval) => clearInterval(interval));
    pingRefs.current.pingIntervals.clear();
    pingRefs.current.sockets.clear();
  }, []);

  useEffect(() => {
    if (!IS_SIGNALR_ENABLED) {
      return;
    }

    if (parseHubConfigs().size === 0) {
      console.log('⏸️ No SignalR hubs configured - skipping connection attempts');
      return;
    }

    if (isAuthenticated) {
      parseHubConfigs().forEach((_, hubName) => {
        void startConnection(hubName);
      });
    } else {
      void stopAllConnections();
    }

    return clearReconnectAndPingResources;
  }, [
    isAuthenticated,
    startConnection,
    stopAllConnections,
    clearReconnectAndPingResources,
  ]);

  useEffect(() => {
    return () => {
      clearReconnectAndPingResources();
      void stopAllConnections();
    };
  }, [stopAllConnections, clearReconnectAndPingResources]);

  return {
    connections,
    connectionStates: new Map(connectionStates),
    getConnection,
    getConnectionState,
    isConnected,
    startConnection,
    stopConnection,
    stopAllConnections,
    invoke,
    invokeHub,
    inspectHandlers,
  };
};
